import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Collapse,
} from "@mui/material";
import { Add, Delete, Save, ExpandLess, ExpandMore } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { update } from "@/actions/setting.action";

export default function PackagesSetting() {
  const dispatch = useDispatch();
  const { settings } = useSelector((state: any) => state.settings);
  const [editedPackages, setEditedPackages] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<boolean[]>([]);

  useEffect(() => {
    const packageSetting = settings.find((s: any) => s.key === "packages");
    if (packageSetting) {
      setEditedPackages(packageSetting.value);
      setExpanded(new Array(packageSetting.value.length).fill(false)); // Tüm paketleri başlangıçta açık tut
    }
  }, [settings]);

  const toggleExpand = (index: number) => {
    setExpanded((prev) => {
      const newExpanded = [...prev];
      newExpanded[index] = !newExpanded[index];
      return newExpanded;
    });
  };

  const handleChange = (index: number, field: string, value: string) => {
    setEditedPackages((prev) => {
      const updatedPackages = [...prev];
      updatedPackages[index] = { ...updatedPackages[index], [field]: value };
      return updatedPackages;
    });
  };

  const handleItemChange = (
    packageIndex: number,
    itemIndex: number,
    value: string
  ) => {
    setEditedPackages((prev) => {
      const updatedPackages = [...prev];
      updatedPackages[packageIndex].items[itemIndex] = value;
      return updatedPackages;
    });
  };

  const handleAddItem = (packageIndex: number) => {
    setEditedPackages((prev) => {
      const updatedPackages = [...prev];
      updatedPackages[packageIndex].items.push("");
      return updatedPackages;
    });
  };

  const handleRemoveItem = (packageIndex: number, itemIndex: number) => {
    setEditedPackages((prev) => {
      const updatedPackages = [...prev];
      updatedPackages[packageIndex].items.splice(itemIndex, 1);
      return updatedPackages;
    });
  };

  const handleAddPackage = () => {
    setEditedPackages((prev) => [
      ...prev,
      {
        title: "",
        description: "",
        price: "",
        items: [""],
        policy: "",
        order: prev.length,
      },
    ]);
    setExpanded((prev) => [...prev, true]); // Yeni paket eklenince açık başlasın
  };

  const handleRemovePackage = (index: number) => {
    setEditedPackages((prev) => prev.filter((_, i) => i !== index));
    setExpanded((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const setting = settings.find((s: any) => s.key === "packages");
    if (setting) {
      dispatch(update(setting._id, { value: editedPackages }) as any);
    }
  };

  return (
    <Box className="p-4 bg-white rounded">
      <Typography variant="h5" gutterBottom>
        Paketler Ayarları
      </Typography>

      {editedPackages.map((pkg, index) => (
        <Paper
          key={index}
          elevation={3}
          style={{ padding: 16, marginBottom: 16, borderRadius: 8 }}
        >
          {/* Üst Başlık ve Aç/Kapat Butonu */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {pkg.title || `Paket ${index + 1}`}
            </Typography>

            <Box display="flex" alignItems="center" gap={1}>
              <IconButton color="primary" onClick={() => toggleExpand(index)}>
                {expanded[index] ? <ExpandLess /> : <ExpandMore />}
              </IconButton>

              <IconButton
                color="secondary"
                onClick={() => handleRemovePackage(index)}
              >
                <Delete />
              </IconButton>
            </Box>
          </Box>

          {/* Paket İçeriğini Aç/Kapat */}
          <Collapse in={expanded[index]}>
            <Box mt={2}>
              <TextField
                fullWidth
                label="Paket Başlığı"
                variant="outlined"
                value={pkg.title}
                onChange={(e) => handleChange(index, "title", e.target.value)}
                className="mb-2"
              />

              <TextField
                fullWidth
                label="Açıklama"
                variant="outlined"
                value={pkg.description}
                onChange={(e) =>
                  handleChange(index, "description", e.target.value)
                }
                className="mb-2"
                multiline
              />

              <TextField
                fullWidth
                label="Fiyat"
                variant="outlined"
                value={pkg.price}
                onChange={(e) => handleChange(index, "price", e.target.value)}
                className="mb-2"
              />

              <TextField
                fullWidth
                label="Politika"
                variant="outlined"
                value={pkg.policy}
                onChange={(e) => handleChange(index, "policy", e.target.value)}
                className="mb-2"
              />

              <Typography variant="subtitle1">Paket İçeriği</Typography>
              {pkg.items.map((item: string, itemIndex: number) => (
                <Box key={itemIndex} display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={item}
                    onChange={(e) =>
                      handleItemChange(index, itemIndex, e.target.value)
                    }
                  />
                  <IconButton
                    color="secondary"
                    onClick={() => handleRemoveItem(index, itemIndex)}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}

              <Button
                variant="contained"
                color="secondary"
                startIcon={<Add />}
                onClick={() => handleAddItem(index)}
                style={{ marginTop: 10 }}
              >
                Yeni İçerik Ekle
              </Button>
            </Box>
          </Collapse>
        </Paper>
      ))}

      <Button
        variant="contained"
        color="primary"
        startIcon={<Add />}
        onClick={handleAddPackage}
      >
        Yeni Paket Ekle
      </Button>

      <Box textAlign="right" mt={3}>
        <Button
          variant="contained"
          color="success"
          startIcon={<Save />}
          onClick={handleSave}
        >
          Kaydet
        </Button>
      </Box>
    </Box>
  );
}
